import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Archive as ArchiveIcon, AlertCircle } from "lucide-react";

/**
 * Lightweight SAP/Fiori-style "Archive with reason" dialog.
 *
 * Generic enough to wrap any soft-archive flow. Parent supplies `onConfirm`
 * which receives `{ reason, notes }` — the dialog handles its own state,
 * validation, and inline error feedback.
 *
 *   <ArchiveDialog
 *     open={!!pendingArchive}
 *     entityLabel="evaluation"
 *     onClose={() => setPendingArchive(null)}
 *     onConfirm={async ({ reason, notes }) => {
 *       await archiveAssessment(pendingArchive.id, {
 *         archive_reason: reason, archive_notes: notes,
 *       });
 *       toast.success("Archived");
 *     }}
 *   />
 *
 * Keep ARCHIVE_REASONS in lockstep with `ALLOWED_ARCHIVE_REASONS` in
 * `/app/backend/audit_utils.py` — the backend rejects unknown values.
 */
export const ARCHIVE_REASONS = [
  "Duplicate Entry",
  "Test / Demo Record",
  "Client Not Responding",
  "Opportunity Closed",
  "Invalid Submission",
  "Internal Assessment",
  "Completed / No Longer Active",
  "Other",
];

export default function ArchiveDialog({
  open,
  onClose,
  onConfirm,
  entityLabel = "record",
  testIdPrefix = "archive-dialog",
}) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setNotes("");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const otherSelected = reason === "Other";
  const trimmedNotes = notes.trim();
  const canSubmit =
    !!reason && !submitting && (!otherSelected || trimmedNotes.length > 0);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!reason) {
      setError("Please select an archive reason.");
      return;
    }
    if (otherSelected && !trimmedNotes) {
      setError("Please describe the reason in the notes field.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await onConfirm({ reason, notes: trimmedNotes });
      onClose?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        `Failed to archive this ${entityLabel}.`;
      setError(typeof detail === "string" ? detail : "Failed to archive.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v && !submitting ? onClose?.() : null)}>
      <DialogContent
        className="max-w-md rounded-sm"
        data-testid={`${testIdPrefix}-content`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1F2937]">
            <ArchiveIcon className="w-4 h-4 text-[#B91C1C]" />
            Archive {entityLabel}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Capture why this {entityLabel} is being archived. The reason and
            any notes are stored on the audit trail and remain visible if
            the {entityLabel} is later restored.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div>
            <label
              htmlFor={`${testIdPrefix}-reason`}
              className="text-[11px] uppercase tracking-wide font-medium text-[#6B7280] mb-1.5 inline-block"
            >
              Archive Reason <span className="text-[#B91C1C]">*</span>
            </label>
            <Select value={reason} onValueChange={(v) => { setReason(v); setError(""); }}>
              <SelectTrigger
                id={`${testIdPrefix}-reason`}
                className="h-9 w-full rounded-sm text-xs"
                data-testid={`${testIdPrefix}-reason-trigger`}
              >
                <SelectValue placeholder="Select a reason…" />
              </SelectTrigger>
              <SelectContent>
                {ARCHIVE_REASONS.map((r) => (
                  <SelectItem
                    key={r}
                    value={r}
                    data-testid={`${testIdPrefix}-reason-${r.replace(/[\s/]+/g, "-").toLowerCase()}`}
                  >
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label
              htmlFor={`${testIdPrefix}-notes`}
              className="text-[11px] uppercase tracking-wide font-medium text-[#6B7280] mb-1.5 inline-block"
            >
              {otherSelected ? (
                <>Notes <span className="text-[#B91C1C]">*</span></>
              ) : (
                <>Notes <span className="text-[#9CA3AF] normal-case">(optional)</span></>
              )}
            </label>
            <textarea
              id={`${testIdPrefix}-notes`}
              data-testid={`${testIdPrefix}-notes`}
              value={notes}
              onChange={(e) => { setNotes(e.target.value); setError(""); }}
              rows={3}
              placeholder={
                otherSelected
                  ? "Describe the archive reason…"
                  : "Add any context for the audit trail…"
              }
              className="w-full rounded-sm border border-[#E5E7EB] text-xs p-2.5 focus:outline-none focus:ring-1 focus:ring-[#025979] resize-none bg-white text-[#1F2937]"
            />
          </div>

          {error && (
            <div
              className="flex items-start gap-1.5 text-[11px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-sm px-2.5 py-1.5"
              data-testid={`${testIdPrefix}-error`}
            >
              <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter className="pt-2 gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={submitting}
              className="rounded-sm text-xs h-9"
              data-testid={`${testIdPrefix}-cancel`}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="rounded-sm text-xs h-9 bg-[#B91C1C] hover:bg-[#991B1B] text-white"
              data-testid={`${testIdPrefix}-confirm`}
            >
              {submitting ? "Archiving…" : "Archive"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
